import { cn } from "@-/com.npmjs.cn";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { useState } from "react";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { ActionMenu } from "@-/dev.improve.haniverse.components.ui.action-menu";
import {
  GlassContentScope,
  ToolbarGlassSurface,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  type LearnCardSlidePages,
  LearnDeckContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";

import { LearnFlashcardActionButton } from "#learn-card-action-button";

/**
 * A slide's pill, shaped like a lesson's: a menu section on the left, the primary action, and a
 * decorative right chevron. The primary reads "Next" until the slide's last view, where it takes the
 * slide's label ("Next Card" by default). Pressing it does what a swipe either way does: it sends
 * the top view off to the left (or, on the last view, dismisses the card). Swiping between views
 * leaves the pill untouched; only the last view's card swipe presses it. The menu's Skip leaves the
 * whole slide from any view.
 */
export function LearnFlashcardSlideControls({
  label,
  pages,
}: {
  label: string;
  pages: LearnCardSlidePages | null;
}) {
  const { diameter, isLandscape } = useToolbarGridMetrics();
  const [commit, isTransitioning] = useContextSelectors(
    LearnDeckContext,
    (c) => c.commit,
    (c) => c.isTransitioning,
  );
  const isLastPage = pages === null || pages.current >= pages.count - 1;
  // The system menu on iOS sizes its host to the trigger, so the square trigger takes the pill's
  // measured height rather than `h-full`.
  const [pillHeight, setPillHeight] = useState(0);

  return (
    // The chevron and the menu overlay the action button's glass, so they share its ink.
    <GlassContentScope>
      <View
        className="relative size-full rounded-full"
        onLayout={({ nativeEvent: { layout } }) => {
          setPillHeight(layout.height);
        }}
      >
        <View className="size-full">
          <LearnFlashcardActionButton
            action={{ kind: "continue" }}
            className={isLandscape ? "px-0" : "px-11"}
            handlesAllActions
            label={isLastPage ? label : "Next"}
            onPress={
              isLastPage || pages === null
                ? undefined
                : () => {
                    pages.goTo(pages.current + 1);
                  }
            }
            swipeDirection={isLastPage ? "both" : undefined}
            testID="slide-lesson-continue-button"
          />
        </View>
        {!isLandscape ? (
          <View
            accessibilityElementsHidden
            className="absolute inset-y-0 right-0 aspect-square h-full items-center justify-center"
            importantForAccessibility="no-hide-descendants"
            pointerEvents="none"
          >
            <Icon
              className={isTransitioning ? "text-neutral-400" : "text-foreground"}
              name="LuChevronRight"
              size={20}
            />
          </View>
        ) : null}
        <View
          className={
            isLandscape
              ? "absolute inset-x-0 top-0 overflow-hidden rounded-full"
              : "absolute inset-y-0 left-0 overflow-hidden rounded-l-full"
          }
        >
          {pillHeight === 0 ? null : (
            <ActionMenu
              align={isLandscape ? "end" : "start"}
              contentClassName="min-w-48"
              disabled={isTransitioning}
              items={[
                {
                  disabled: isTransitioning,
                  label: "Skip",
                  onPress: () => commit({ kind: "continue" }, "button"),
                  systemImage: "forward",
                  testID: "slide-lesson-skip-button",
                },
              ]}
              side="top"
              sideOffset={8}
              trigger={
                <Pressable
                  accessibilityLabel="More slide actions"
                  accessibilityRole="button"
                  accessibilityState={{ disabled: isTransitioning }}
                  className={cn(
                    "items-center justify-center overflow-hidden",
                    isLandscape ? "rounded-full" : "rounded-l-full border-r border-border",
                    !isTransitioning && "active:bg-accent",
                    isTransitioning && "cursor-not-allowed",
                  )}
                  disabled={isTransitioning}
                  style={{
                    height: isLandscape ? diameter : pillHeight,
                    width: isLandscape ? diameter : pillHeight,
                  }}
                  testID="slide-card-menu"
                >
                  {isLandscape ? (
                    <ToolbarGlassSurface
                      className="absolute inset-0"
                      interactive={false}
                      pointerEvents="none"
                    />
                  ) : null}
                  <Icon
                    className={isTransitioning ? "text-neutral-400" : "text-foreground"}
                    name="FiMoreHorizontal"
                    size={20}
                  />
                </Pressable>
              }
            />
          )}
        </View>
      </View>
    </GlassContentScope>
  );
}
